import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { translationsAPI, supervisorAPI, validationsAPI, suggestionsAPI } from '../services/api';
import Navbar from '../components/common/Navbar';
import TranslationCard from '../components/translation/TranslationCard';
import ReannotationPanel from '../components/common/ReannotationPanel';

const REVIEW_BADGE = {
  pending:  { label: 'Sin revisar', cls: 'bg-gray-100 text-gray-600' },
  approved: { label: 'Aprobado ✓', cls: 'bg-green-100 text-green-700' },
  returned: { label: 'Devuelto ↩', cls: 'bg-yellow-100 text-yellow-700' },
  rejected: { label: 'Rechazado ✗', cls: 'bg-red-100 text-red-700' },
};

const CORRECTNESS_LABELS = {
  correct: 'Correcta',
  incorrect: 'Incorrecta',
  gn_more: 'Guaraní tiene más info',
  es_more: 'Español tiene más info',
};

// ---------------------------------------------------------------------------
// Shared: special Guaraní characters toolbar
// ---------------------------------------------------------------------------
const SPECIAL_CHARS = [
  '\u00e3','\u1ebd','\u0129','\u00f5','\u0169','\u1ef9',
  '\u00e1','\u00e9','\u00ed','\u00f3','\u00fa','\u00fd',
  'g\u0303','G\u0303','\u00f1','\u00d1',
  { label: "pus\u00f3 ( ' )", value: "'" }
];

function insertCharAt(ref, fieldName, ch, setForm) {
  const el = ref.current;
  if (!el) return;
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  const newVal = el.value.slice(0, start) + ch + el.value.slice(end);
  setForm(prev => ({ ...prev, [fieldName]: newVal }));
  el.value = newVal;
  const pos = start + ch.length;
  el.focus();
  el.setSelectionRange(pos, pos);
}

function SpecialCharsToolbar({ targetRef, fieldName, setForm }) {
  return (
    <div className="flex flex-wrap gap-1 mb-1">
      {SPECIAL_CHARS.map((item) => {
        const isObj = typeof item === 'object' && item !== null;
        const label = isObj ? item.label : item;
        const value = isObj ? item.value : item;
        return (
          <button
            key={label}
            type="button"
            onClick={() => insertCharAt(targetRef, fieldName, value, setForm)}
            className="text-xs px-2 py-0.5 bg-white border rounded shadow-sm hover:bg-gray-100"
            aria-label={`Insertar ${label}`}
          >{label}</button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline edit form for a returned validation
// ---------------------------------------------------------------------------
function ReturnedValidationForm({ item, onSaved }) {
  const [form, setForm] = useState({
    correctness: item.correctness,
    fluency_score: item.fluency_score,
    accuracy_score: item.accuracy_score,
    notes: item.notes || '',
    category: item.category || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const notesRef = useRef(null);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const flu = Number(form.fluency_score);
    const acc = Number(form.accuracy_score);
    if (!Number.isInteger(flu) || flu < 1 || flu > 5) {
      setError('La fluidez debe ser un número entero entre 1 y 5.');
      return;
    }
    if (!Number.isInteger(acc) || acc < 1 || acc > 5) {
      setError('La precisión debe ser un número entero entre 1 y 5.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await validationsAPI.update(item.id, {
        correctness: form.correctness,
        fluency_score: flu,
        accuracy_score: acc,
        notes: form.notes || null,
        category: form.category || null,
      });
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4 pt-4 border-t border-yellow-200 space-y-3">
      <p className="text-xs font-semibold text-yellow-700 uppercase tracking-wide">Corregir validación</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Corrección</label>
          <select
            value={form.correctness}
            onChange={e => handleChange('correctness', e.target.value)}
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
          >
            {Object.entries(CORRECTNESS_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Fluidez (1–5)</label>
          <input
            type="number" min={1} max={5}
            value={form.fluency_score}
            onChange={e => handleChange('fluency_score', e.target.value)}
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Precisión (1–5)</label>
          <input
            type="number" min={1} max={5}
            value={form.accuracy_score}
            onChange={e => handleChange('accuracy_score', e.target.value)}
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Notas (opcional)</label>
        <SpecialCharsToolbar targetRef={notesRef} fieldName="notes" setForm={setForm} />
        <textarea
          ref={notesRef}
          value={form.notes}
          onChange={e => handleChange('notes', e.target.value)}
          rows={2}
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
        />
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <button
        type="submit"
        disabled={saving}
        className="px-4 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium rounded disabled:opacity-50"
      >
        {saving ? 'Guardando…' : '↩ Re-enviar al supervisor'}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Inline edit form for a returned suggestion
// ---------------------------------------------------------------------------
function ReturnedSuggestionForm({ item, onSaved }) {
  const [form, setForm] = useState({
    suggested_guarani: item.suggested_guarani || '',
    suggested_spanish: item.suggested_spanish || '',
    reason: item.reason || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const guaraniRef = useRef(null);
  const spanishRef = useRef(null);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.suggested_guarani?.trim() && !form.suggested_spanish?.trim()) {
      setError('Debés completar al menos una de las dos traducciones.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await suggestionsAPI.update(item.id, {
        suggested_guarani: form.suggested_guarani || null,
        suggested_spanish: form.suggested_spanish || null,
        reason: form.reason || null,
      });
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4 pt-4 border-t border-yellow-200 space-y-3">
      <p className="text-xs font-semibold text-yellow-700 uppercase tracking-wide">Corregir sugerencia</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Traducción al guaraní</label>
          <SpecialCharsToolbar targetRef={guaraniRef} fieldName="suggested_guarani" setForm={setForm} />
          <textarea
            ref={guaraniRef}
            value={form.suggested_guarani}
            onChange={e => handleChange('suggested_guarani', e.target.value)}
            rows={2}
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Traducción al español</label>
          {/* Spacer to match the height of the SpecialCharsToolbar in the guaraní column */}
          <div className="h-6 mb-1" aria-hidden="true"></div>
          <textarea
            ref={spanishRef}
            value={form.suggested_spanish}
            onChange={e => handleChange('suggested_spanish', e.target.value)}
            rows={2}
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Razón (opcional)</label>
        <textarea
          value={form.reason}
          onChange={e => handleChange('reason', e.target.value)}
          rows={2}
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
        />
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <button
        type="submit"
        disabled={saving}
        className="px-4 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium rounded disabled:opacity-50"
      >
        {saving ? 'Guardando…' : '↩ Re-enviar al supervisor'}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Card for a returned item (validation or suggestion)
// ---------------------------------------------------------------------------
function ReturnedItemCard({ entry, onSaved }) {
  const { translation, item, notes, supervisor_username, target_type } = entry;

  return (
    <div id={`returned-item-${entry.target_id}`} className="bg-white rounded-lg shadow-sm border border-yellow-300 p-6 transition-all duration-500">
      {/* Translation preview */}
      {translation && (
        <div className="mb-3 p-3 bg-gray-50 rounded text-sm">
          <div className="flex gap-6">
            {translation.guarani_text && (
              <div><span className="font-medium text-gray-500 text-xs uppercase">Guaraní</span><p className="mt-0.5 text-gray-800">{translation.guarani_text}</p></div>
            )}
            {translation.spanish_text && (
              <div><span className="font-medium text-gray-500 text-xs uppercase">Español</span><p className="mt-0.5 text-gray-800">{translation.spanish_text}</p></div>
            )}
          </div>
        </div>
      )}

      {/* Review feedback */}
      <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded mb-1">
        <span className="text-yellow-500 mt-0.5">↩</span>
        <div className="text-sm">
          <span className="font-medium text-yellow-800">
            {supervisor_username ? `${supervisor_username} devolvió esta ${target_type === 'validation' ? 'validación' : 'sugerencia'}` : 'Devuelto para corrección'}
          </span>
          {notes && <p className="text-yellow-700 mt-0.5 italic">"{notes}"</p>}
        </div>
      </div>

      {/* Current values summary */}
      {target_type === 'validation' && (
        <div className="text-xs text-gray-500 mt-2 flex gap-4">
          <span>Corrección: <strong>{CORRECTNESS_LABELS[item.correctness] || item.correctness}</strong></span>
          <span>Fluidez: <strong>{item.fluency_score}/5</strong></span>
          <span>Precisión: <strong>{item.accuracy_score}/5</strong></span>
        </div>
      )}
      {target_type === 'suggestion' && (
        <div className="text-xs text-gray-500 mt-2 flex gap-4">
          {item.suggested_guarani && <span>Guaraní: <strong>{item.suggested_guarani.slice(0, 60)}…</strong></span>}
          {item.suggested_spanish && <span>Español: <strong>{item.suggested_spanish.slice(0, 60)}…</strong></span>}
        </div>
      )}

      {/* Edit form */}
      {target_type === 'validation' ? (
        <ReturnedValidationForm item={item} onSaved={onSaved} />
      ) : (
        <ReturnedSuggestionForm item={item} onSaved={onSaved} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Review badge shown in the "Mis validaciones" tab
// ---------------------------------------------------------------------------
const ReviewBadge = ({ review }) => {
  if (!review) return null;
  const badge = REVIEW_BADGE[review.status] || REVIEW_BADGE.pending;
  return (
    <div className="mt-3 pt-3 border-t border-yellow-100 flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500">Revisión del profesor:</span>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.label}</span>
        {review.supervisor_username && (
          <span className="text-xs text-gray-400">— {review.supervisor_username}</span>
        )}
      </div>
      {review.notes && (
        <p className="text-xs text-yellow-800 italic">"{review.notes}"</p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const HistoryPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('returned');
  const [returnedItems, setReturnedItems] = useState([]);
  const [validatedTranslations, setValidatedTranslations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewMap, setReviewMap] = useState({});
  const [pagination, setPagination] = useState({
    validated: { skip: 0, limit: 20, total: 0, hasNext: false },
  });

  const loadReturned = useCallback(async () => {
    try {
      const res = await supervisorAPI.getMyReturned();
      setReturnedItems(res.data || []);
      // If there are returned items, keep that tab active; otherwise switch to validated
      if ((res.data || []).length === 0 && activeTab === 'returned') {
        setActiveTab('validated');
      }
    } catch {
      setReturnedItems([]);
    }
  }, []);

  const loadValidatedTranslations = async (skip = 0) => {
    setLoading(true);
    try {
      const response = await translationsAPI.getMyValidatedHistory(skip, 20);
      setValidatedTranslations(response.data.items);
      setPagination(prev => ({
        ...prev,
        validated: {
          skip: response.data.skip,
          limit: response.data.limit,
          total: response.data.total,
          hasNext: response.data.has_next,
        },
      }));
    } catch (error) {
      console.error('Error cargando traducciones evaluadas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadReturned();
      loadValidatedTranslations();
      supervisorAPI.getMyReviews()
        .then(res => {
          const map = {};
          (res.data || []).forEach(r => {
            if (r.target_type === 'validation') map[r.target_id] = r;
          });
          setReviewMap(map);
        })
        .catch(() => {});
    }
  }, [user]);

  // Scroll to a specific returned item if ?returned=<target_id> is in the URL
  useEffect(() => {
    const targetId = searchParams.get('returned');
    if (!targetId) return;
    // Wait for the list to render, then scroll
    let ringTimer;
    const timer = setTimeout(() => {
      const el = document.getElementById(`returned-item-${targetId}`);
      if (el) {
        setActiveTab('returned');
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-yellow-400');
        ringTimer = setTimeout(() => el.classList.remove('ring-2', 'ring-yellow-400'), 3000);
      }
    }, 400);
    return () => {
      clearTimeout(timer);
      clearTimeout(ringTimer);
    };
  }, [searchParams, returnedItems]);

  const handleResubmitted = () => {
    loadReturned();
    loadValidatedTranslations();
    supervisorAPI.getMyReviews()
      .then(res => {
        const map = {};
        (res.data || []).forEach(r => {
          if (r.target_type === 'validation') map[r.target_id] = r;
        });
        setReviewMap(map);
      })
      .catch(() => {});
  };

  const handleNextPage = () => {
    const cur = pagination.validated;
    loadValidatedTranslations(cur.skip + cur.limit);
  };

  const handlePrevPage = () => {
    const cur = pagination.validated;
    loadValidatedTranslations(Math.max(0, cur.skip - cur.limit));
  };

  const currentPagination = pagination.validated;
  const currentTranslations = validatedTranslations;

  const tabs = [
    {
      key: 'returned',
      label: '↩ Por corregir',
      count: returnedItems.length,
      badge: returnedItems.length > 0 ? 'bg-red-500' : null,
    },
    { key: 'validated', label: 'Mis validaciones', count: pagination.validated.total },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Historial</h1>
          <p className="mt-2 text-gray-600">Revisá tu actividad y corregí lo que el supervisor devolvió</p>
        </div>

        <ReannotationPanel />

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${tab.badge || 'bg-gray-200 text-gray-600'} text-white`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* ---- TAB: Por corregir ---- */}
        {activeTab === 'returned' && (
          <div className="space-y-6">
            {returnedItems.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-gray-400 text-lg">✓ No tenés ítems pendientes de corrección</p>
              </div>
            ) : (
              returnedItems.map(entry => (
                <ReturnedItemCard
                  key={`${entry.target_type}-${entry.target_id}`}
                  entry={entry}
                  onSaved={handleResubmitted}
                />
              ))
            )}
          </div>
        )}

        {/* ---- TAB: Mis validaciones ---- */}
        {activeTab === 'validated' && (
          <>
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                <div className="space-y-6 mb-8">
                  {currentTranslations.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500">Aún no has evaluado ninguna traducción</p>
                    </div>
                  ) : (
                    currentTranslations.map((item) => (
                      <div
                        key={activeTab === 'validated' ? item.validation.id : item.id}
                        className="bg-white rounded-lg shadow-sm border p-6"
                      >
                        <TranslationCard
                          translation={activeTab === 'validated' ? item.translation : item}
                        />
                        {activeTab === 'validated' && item.validation && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="font-medium text-gray-700">Corrección:</span>
                                <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                                  item.validation.correctness === 'correct'
                                    ? 'bg-green-100 text-green-800'
                                    : item.validation.correctness === 'incorrect'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {item.validation.correctness}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">Fluidez:</span>
                                <span className="ml-2">{item.validation.fluency_score}/5</span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">Precisión:</span>
                                <span className="ml-2">{item.validation.accuracy_score}/5</span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">Evaluado:</span>
                                <span className="ml-2">
                                  {new Date(item.validation.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            {item.validation.notes && (
                              <div className="mt-3">
                                <span className="font-medium text-gray-700">Notas:</span>
                                <p className="mt-1 text-gray-600">{item.validation.notes}</p>
                              </div>
                            )}
                            <ReviewBadge review={reviewMap[item.validation.id]} />
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {currentTranslations.length > 0 && (
                  <div className="flex items-center justify-between bg-white px-4 py-3 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-700">
                      Mostrando{' '}
                      <span className="font-medium">{currentPagination.skip + 1}</span>
                      {' '}a{' '}
                      <span className="font-medium">
                        {Math.min(currentPagination.skip + currentPagination.limit, currentPagination.total)}
                      </span>
                      {' '}de{' '}
                      <span className="font-medium">{currentPagination.total}</span>
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={handlePrevPage}
                        disabled={currentPagination.skip === 0}
                        className="px-3 py-1.5 border border-gray-300 text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={handleNextPage}
                        disabled={!currentPagination.hasNext}
                        className="px-3 py-1.5 border border-gray-300 text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Siguiente
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HistoryPage;
